import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories, subCategoriesMap } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const catName = category?.name || slug || '';

  const [activeSub, setActiveSub] = useState('全部');
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveSub('全部');
    setVisibleCount(12);
  }, [slug]);

  const subCategories = useMemo(() => {
    if (!slug) return [];
    return subCategoriesMap[slug] || ['全部'];
  }, [slug]);

  const filteredMovies = useMemo(() => {
    let result = movies.filter((m) => m.category === slug);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(query) ||
          m.description.toLowerCase().includes(query) ||
          m.actors.some((a) => a.toLowerCase().includes(query))
      );
    }

    if (activeSub !== '全部') {
      result = result.filter((m) => m.subCategory === activeSub);
    }

    return result;
  }, [slug, activeSub, searchQuery]);

  const displayedMovies = filteredMovies.slice(0, visibleCount);
  const hasMore = visibleCount < filteredMovies.length;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: catName },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: '首页',
                      item: 'https://www.yayacinema.com/',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: catName,
                    },
                  ],
                }),
              }}
            />

            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {searchQuery ? `搜索: ${searchQuery}` : catName}
              </h1>
              <p className="text-sm text-foreground-500">
                {searchQuery
                  ? `找到 ${filteredMovies.length} 个相关结果`
                  : `共 ${filteredMovies.length} 部${catName}资源`}
              </p>
            </div>

            <div className="flex items-center gap-1.5 overflow-x-auto pb-3 mb-6 scrollbar-hide flex-wrap">
              {subCategories.map((sub) => (
                <button
                  key={sub}
                  onClick={() => {
                    setActiveSub(sub);
                    setVisibleCount(12);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSub === sub
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>

            {displayedMovies.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                  {displayedMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                {hasMore && (
                  <div className="text-center mt-8 mb-10">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 12)}
                      className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-8 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-down-line"></i>
                      加载更多 ({filteredMovies.length - visibleCount} 部)
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-search-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}